import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { PostalCodeInfo } from '@app/domain/models/postal-code-info';
import { PostalCodeRepository } from '@app/domain/ports/postal-code.repository';
import { SKIP_API_KEY } from '@app/data/data-sources/remote/postal-code-remote.data-source';

/** Shape on disk: objeto plano keyed por CP. Cada entry trae estado +
 *  municipio (compartidos para todas las colonias del CP) + lista de
 *  colonias. Mantenido fuera de TypeScript decorators porque es JSON puro. */
interface SepomexLocalDataset {
  [cp: string]: {
    estado: string;
    municipio: string;
    colonias: string[];
  };
}

const DATASET_URL = '/assets/sepomex.json';

/**
 * Implementación local del `PostalCodeRepository`. Lazy-loadea `/assets/sepomex.json`
 * en el primer lookup y cachea el dataset en memoria. Lookups posteriores son
 * O(1) (acceso por hash en el objeto).
 *
 * Ventajas vs HTTP a un tercero:
 * - Cero CORS, cero rate limits, cero leak de PII (el CP no sale del front).
 * - Disponible offline tras la primera descarga (caché del browser/CDN).
 * - Sin SLA de un servicio externo.
 *
 * Tamaño esperado del dataset completo: ~5–8 MB (sin gzip), ~1.5–2 MB con
 * compresión (CloudFront/nginx ya gzipean assets). El payload se descarga una
 * sola vez por sesión y solo cuando el usuario llega a un step con CP.
 *
 * Diseño defensivo: cualquier error (404, JSON inválido, red caída) se atrapa
 * y emite `null`. La UI deja al usuario capturar manualmente sin error visible.
 */
@Injectable()
export class PostalCodeLocalDataSource extends PostalCodeRepository {
  private readonly http = inject(HttpClient);
  private dataset: SepomexLocalDataset | null = null;
  private loadPromise: Promise<SepomexLocalDataset | null> | null = null;

  override lookup(cp: string): Observable<PostalCodeInfo | null> {
    return from(this.ensureDataset()).pipe(
      map((dataset) => {
        if (!dataset) return null;
        const entry = dataset[cp];
        if (!entry) return null;
        return {
          cp,
          estado: entry.estado,
          municipio: entry.municipio,
          colonias: entry.colonias,
        };
      }),
      catchError(() => of(null)),
    );
  }

  /** Resuelve al dataset cacheado. Concurrent-safe: múltiples llamadas en
   *  paralelo durante la primera carga comparten la misma promesa. */
  private async ensureDataset(): Promise<SepomexLocalDataset | null> {
    if (this.dataset) return this.dataset;
    if (this.loadPromise) return this.loadPromise;
    this.loadPromise = this.loadDataset().then(
      (data) => {
        this.dataset = data;
        return data;
      },
      () => null,
    );
    return this.loadPromise;
  }

  private async loadDataset(): Promise<SepomexLocalDataset> {
    // SKIP_API_KEY: el dataset vive en `/assets/`, mismo origin del front;
    // no necesitamos enviar el x-api-key del backend al pedirlo.
    const context = new HttpContext().set(SKIP_API_KEY, true);
    const data = await new Promise<SepomexLocalDataset>((resolve, reject) => {
      this.http
        .get<SepomexLocalDataset>(DATASET_URL, { context })
        .subscribe({ next: resolve, error: reject });
    });
    return data;
  }
}
